import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Download,
  Eye,
  FileText,
  Lock,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import type {
  WorkerDocumentRetentionRule,
  WorkerDocumentSensitivity,
  WorkerDocumentType,
} from "@crewledger/shared/schemas";
import { WORKER_DOCUMENT_TYPE_LABELS } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { fetchWorker } from "./api";
import { WorkerDetailTabs } from "./components/worker-detail-tabs";
import {
  deleteWorkerDocument,
  downloadWorkerDocument,
  fetchWorkerDocuments,
  uploadWorkerDocument,
  viewWorkerDocument,
} from "./worker-documents-api";
import { useWorkerDocumentPermissions } from "./use-worker-document-permissions";

const DOCUMENT_TYPES = Object.keys(WORKER_DOCUMENT_TYPE_LABELS) as WorkerDocumentType[];
const RETENTION_RULES: Array<{ value: WorkerDocumentRetentionRule; label: string }> = [
  { value: "I9_US_TEMPLATE", label: "I-9 US template" },
  { value: "W4_US_TEMPLATE", label: "W-4 US template" },
  { value: "FIELD_CERTIFICATION_EXPIRATION", label: "Field certification expiration" },
  { value: "DRIVER_LICENSE_EXPIRATION", label: "Driver license expiration" },
  { value: "CUSTOM", label: "Custom" },
];
const DOCUMENT_TABS = [
  { key: "FIELD", label: "Field Documents", sensitivities: ["FIELD"] },
  { key: "HR", label: "HR Documents", sensitivities: ["HR", "CONFIDENTIAL"] },
  { key: "TAX", label: "Tax Documents", sensitivities: ["TAX"] },
  { key: "IDENTITY", label: "Identity Documents", sensitivities: ["IDENTITY"] },
] as const;

type DocumentTab = (typeof DOCUMENT_TABS)[number]["key"];

export function WorkerDocumentsPage() {
  const { workerId } = useParams();
  const { toast } = useToast();
  const { canRead, canWrite, canViewAllTypes } = useWorkerDocumentPermissions();
  const [workerName, setWorkerName] = useState("");
  const [documents, setDocuments] = useState<Awaited<
    ReturnType<typeof fetchWorkerDocuments>
  >["data"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DocumentTab>("FIELD");
  const [documentType, setDocumentType] = useState<WorkerDocumentType>("OTHER");
  const [expiresAt, setExpiresAt] = useState("");
  const [retentionRule, setRetentionRule] =
    useState<WorkerDocumentRetentionRule>("CUSTOM");
  const [retentionUntil, setRetentionUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deletionReason, setDeletionReason] = useState("");

  const load = () => {
    if (!workerId) return;
    setLoading(true);
    setError(null);
    Promise.all([fetchWorker(workerId), fetchWorkerDocuments(workerId)])
      .then(([workerRes, docsRes]) => {
        setWorkerName(workerRes.data.displayName);
        setDocuments(docsRes.data);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load documents");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(load, [workerId]);

  const availableTypes = DOCUMENT_TYPES.filter((type) => {
    if (canViewAllTypes) return true;
    return type === "CERTIFICATION" || type === "SAFETY_CARD" || type === "EQUIPMENT_LICENSE";
  });

  const visibleDocuments = useMemo(() => {
    const tab = DOCUMENT_TABS.find((item) => item.key === activeTab)!;
    return (
      documents?.items.filter((document) =>
        (tab.sensitivities as readonly WorkerDocumentSensitivity[]).includes(
          document.sensitivity,
        ),
      ) ?? []
    );
  }, [activeTab, documents]);

  const handleUpload = async () => {
    if (!workerId || !file) return;
    setUploading(true);
    try {
      await uploadWorkerDocument(workerId, {
        file,
        documentType,
        expiresAt: expiresAt || null,
        retentionRule,
        retentionUntil: retentionUntil || null,
        notes: notes || null,
      });
      toast({ variant: "success", title: "Document uploaded" });
      setFile(null);
      setExpiresAt("");
      setRetentionRule("CUSTOM");
      setRetentionUntil("");
      setNotes("");
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Upload failed",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!workerId || !deletingId || !deletionReason.trim()) return;
    try {
      await deleteWorkerDocument(workerId, deletingId, deletionReason);
      toast({ variant: "success", title: "Document deleted" });
      setDeletingId(null);
      setDeletionReason("");
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Delete failed",
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  if (!canRead) {
    return (
      <EmptyState
        icon={Lock}
        title="Access denied"
        description="Worker documents are sensitive and require an explicit document permission."
      />
    );
  }

  if (error && !documents) {
    return (
      <EmptyState
        icon={Lock}
        title={error.toLowerCase().includes("access") ? "Access denied" : "Could not load documents"}
        description={error}
        actionLabel="Back to worker"
        onAction={() => {
          window.history.back();
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={workerName || "Worker documents"}
        description="Optional onboarding and compliance documents."
        actions={
          <Link to={`/workers/${workerId}`}>
            <Button variant="outline" size="sm">
              Back to overview
            </Button>
          </Link>
        }
      />

      <WorkerDetailTabs />

      <div className="grid gap-2 rounded-xl border border-navy-blueprint/15 bg-bg-light p-4 text-sm text-navy-deep md:grid-cols-3">
        <p className="flex items-start gap-2">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          Sensitive documents are private and access-controlled.
        </p>
        <p className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          Werkio never exposes document files through public links.
        </p>
        <p className="flex items-start gap-2">
          <FileText className="mt-0.5 h-4 w-4 shrink-0" />
          Every document download is recorded in the audit trail.
        </p>
      </div>

      {!canViewAllTypes ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Limited access: company policy permits only approved field documents. I-9, W-4,
          applications, and identity documents remain restricted.
        </p>
      ) : null}

      {documents ? <ComplianceSummary compliance={documents.compliance} /> : null}

      {canWrite && documents?.canUpload ? (
        <div className="rounded-xl border bg-card-white p-4 shadow-[var(--shadow-card)]">
          <h2 className="flex items-center gap-2 text-base font-semibold text-navy-deep">
            <Upload className="h-4 w-4" />
            Upload document
          </h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Document type</Label>
              <select
                value={documentType}
                onChange={(event) => {
                  setDocumentType(event.target.value as WorkerDocumentType);
                }}
                className="flex h-10 w-full rounded-md border border-input px-3 text-sm"
              >
                {availableTypes.map((type) => (
                  <option key={type} value={type}>
                    {WORKER_DOCUMENT_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Expiration date</Label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(event) => {
                  setExpiresAt(event.target.value);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Retention template</Label>
              <select
                value={retentionRule}
                onChange={(event) => {
                  setRetentionRule(event.target.value as WorkerDocumentRetentionRule);
                }}
                className="flex h-10 w-full rounded-md border border-input px-3 text-sm"
              >
                {RETENTION_RULES.map((rule) => (
                  <option key={rule.value} value={rule.value}>
                    {rule.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Retain until</Label>
              <Input
                type="date"
                value={retentionUntil}
                onChange={(event) => {
                  setRetentionUntil(event.target.value);
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground md:col-span-2">
              Retention templates are operational helpers. Your company is responsible for
              confirming legal retention requirements.
            </p>
            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Input
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                }}
                placeholder="Optional notes"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>File</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic,image/heif"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                }}
              />
              <p className="text-xs text-muted-foreground">
                PDF, JPG, JPEG, PNG, or HEIC. Maximum size is configured by your company
                (10 MB by default).
              </p>
            </div>
          </div>
          <Button
            className="mt-4"
            disabled={!file || uploading}
            onClick={() => void handleUpload()}
          >
            {uploading ? "Uploading…" : "Upload document"}
          </Button>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
        <div className="flex gap-1 overflow-x-auto border-b bg-bg-light p-2">
          {DOCUMENT_TABS.map((tab) => {
            const restricted = !canViewAllTypes && tab.key !== "FIELD";
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold",
                  activeTab === tab.key
                    ? "bg-navy-deep text-white"
                    : "text-navy-deep hover:bg-white",
                )}
              >
                {restricted ? <Lock className="h-3.5 w-3.5 opacity-80" /> : null}
                {tab.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="h-40 animate-pulse" />
        ) : !canViewAllTypes && activeTab !== "FIELD" ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <Lock className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-semibold text-navy-deep">Restricted category</p>
            <p className="max-w-md text-sm text-muted-foreground">
              {activeTab === "HR"
                ? "HR documents (applications and confidential files) require payroll or admin access."
                : activeTab === "TAX"
                  ? "Tax documents (W-4) require payroll or admin access."
                  : "Identity documents (I-9, driver license) require admin access."}{" "}
              Site managers cannot view this category by default.
            </p>
          </div>
        ) : visibleDocuments.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No documents in this category. Worker documents remain optional during beta.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[880px] text-sm">
              <thead>
                <tr className="border-b bg-bg-light text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3">Uploaded</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleDocuments.map((document) => (
                  <tr key={document.id} className="border-b border-border/60 last:border-0">
                    <td className="px-4 py-3 font-medium">
                      <span className="flex items-center gap-1.5">
                        {document.sensitivity !== "FIELD" ? (
                          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : null}
                        {WORKER_DOCUMENT_TYPE_LABELS[document.documentType]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {document.fileName}
                      <p className="text-xs text-muted-foreground">
                        {formatBytes(document.fileSize)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <DocumentStatus status={document.displayStatus} />
                      {document.scanStatus === "SCAN_FAILED" ? (
                        <p className="mt-1 text-xs font-medium text-amber-700">
                          Admin warning: malware scan failed.
                        </p>
                      ) : null}
                      {document.scanStatus === "QUARANTINED" ? (
                        <p className="mt-1 text-xs font-medium text-red-700">Quarantined</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">{document.expiresAt ?? "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {document.uploadedByName ?? "—"}
                      <br />
                      <span className="text-xs">
                        {new Date(document.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            document.scanStatus === "QUARANTINED" ||
                            document.scanStatus === "PENDING_SCAN"
                          }
                          onClick={() => {
                            void viewWorkerDocument(workerId!, document.id).catch(
                              (err: unknown) => {
                                toast({
                                  variant: "error",
                                  title: "View failed",
                                  description: err instanceof Error ? err.message : undefined,
                                });
                              },
                            );
                          }}
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            document.scanStatus === "QUARANTINED" ||
                            document.scanStatus === "PENDING_SCAN"
                          }
                          onClick={() => {
                            const requiresReason =
                              documents?.requireDownloadReasonForSensitiveDocs &&
                              document.sensitivity !== "FIELD";
                            const reason = requiresReason
                              ? window.prompt(
                                  "Reason for downloading this sensitive document (required):",
                                )
                              : undefined;
                            if (requiresReason && !reason?.trim()) return;
                            void downloadWorkerDocument(
                              workerId!,
                              document.id,
                              document.fileName,
                              reason ?? undefined,
                            ).catch((err: unknown) => {
                              toast({
                                variant: "error",
                                title: "Download failed",
                                description: err instanceof Error ? err.message : undefined,
                              });
                            });
                          }}
                        >
                          <Download className="mr-1 h-3.5 w-3.5" />
                          Download
                        </Button>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-danger"
                            onClick={() => setDeletingId(document.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deletingId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-deep/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-card-white p-5 shadow-[var(--shadow-elevated)]">
            <h2 className="text-lg font-semibold text-navy-deep">Delete document?</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              The document will be soft-deleted and hidden from normal views.
            </p>
            <div className="mt-4 space-y-2">
              <Label>Deletion reason</Label>
              <Input
                value={deletionReason}
                onChange={(event) => setDeletionReason(event.target.value)}
                placeholder="Required"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeletingId(null);
                  setDeletionReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={!deletionReason.trim()}
                onClick={() => void handleDelete()}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ComplianceSummary({
  compliance,
}: {
  compliance: NonNullable<
    Awaited<ReturnType<typeof fetchWorkerDocuments>>["data"]
  >["compliance"];
}) {
  const cards = [
    {
      label: "Field documents complete",
      value: compliance.fieldDocumentsComplete ? "Yes" : "No",
    },
    {
      label: "HR documents complete",
      value: compliance.hrDocumentsComplete ? "Yes" : "No",
    },
    { label: "Expiring soon", value: String(compliance.expiringSoon) },
    {
      label: "Missing required documents",
      value: String(compliance.missingRequiredDocuments.length),
    },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <div key={card.label} className="rounded-xl border bg-card-white px-4 py-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{card.label}</p>
          <p className="mt-1 text-xl font-bold text-navy-deep">{card.value}</p>
        </div>
      ))}
    </div>
  );
}

function DocumentStatus({
  status,
}: {
  status: "VALID" | "EXPIRING_SOON" | "EXPIRED" | "MISSING";
}) {
  const labels = {
    VALID: "Valid",
    EXPIRING_SOON: "Expiring Soon",
    EXPIRED: "Expired",
    MISSING: "Missing",
  };
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-semibold",
        status === "VALID" && "bg-emerald-100 text-emerald-800",
        status === "EXPIRING_SOON" && "bg-amber-100 text-amber-800",
        status === "EXPIRED" && "bg-red-100 text-red-800",
        status === "MISSING" && "bg-slate-100 text-slate-700",
      )}
    >
      {labels[status]}
    </span>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${Math.round((bytes / 1024 / 1024) * 10) / 10} MB`;
}
